// SHADOW MARKET™ — Product detail page aligned with the main REBEL site.
// Inter, orange-amber CTAs, glass dark cards. Fully i18n (ES/EN/SK).
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Plus, Minus, Check, X, ArrowLeft, Ruler, Sparkles } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import {
  getProductBySlug,
  getVariantsOfType,
  SIZE_GUIDE,
  FREE_SHIPPING_THRESHOLD,
  SHIPPING_COST,
} from "../data/shop-products";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { useShopI18n, typeLabelPlural } from "../i18n/shop";
import "./shadow-market.css";

const Accordion = ({ title, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-white/10">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between py-3.5 text-[11px] font-black uppercase tracking-[0.18em] text-white transition-colors hover:text-orange-200"
        data-testid={testid}
      >
        <span>{title}</span>
        <span className="text-white/45">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="pb-4 text-sm leading-relaxed text-white/70">
          {children}
        </div>
      )}
    </div>
  );
};

const ShadowMarketProductPage = () => {
  const { slug } = useParams();
  const product = getProductBySlug(slug);
  const { addItem, openCart } = useCart();
  const { isPro } = useAuth();
  const { lang } = useLanguage();
  const t = useShopI18n(lang);
  const [selectedSize, setSelectedSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const variants = useMemo(
    () => (product ? getVariantsOfType(product.type) : []),
    [product]
  );

  if (!product) {
    return (
      <div className="shop-root flex min-h-screen flex-col bg-black text-white">
        <Navbar />
        <main className="flex flex-1 items-center justify-center px-6 py-32 text-center">
          <div>
            <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-white/45">
              404 · {t.product_not_found}
            </p>
            <Link
              to="/shadow-market"
              className="text-sm font-black uppercase tracking-[0.18em] text-orange-300"
              style={{ borderBottom: "1px solid currentColor", paddingBottom: 2 }}
            >
              {t.product_back_store}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isOut = (s) => product.soldOutSizes?.includes(s);
  const typeLabel = typeLabelPlural(lang, product.type);

  const handleAdd = () => {
    const size = selectedSize || product.sizes.find((s) => !isOut(s));
    if (!size) return;
    addItem(product, size, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
    setTimeout(() => openCart(), 250);
  };

  return (
    <div className="shop-root min-h-screen bg-black text-white antialiased" data-testid="shadow-market-product-page">
      <Navbar />
      <div style={{ height: 96 }} />

      {/* Breadcrumb */}
      <div className="mx-auto max-w-6xl px-6 py-4 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40 md:px-10">
        <Link to="/shadow-market" className="transition-colors hover:text-orange-300">
          SHADOW MARKET™
        </Link>
        <span className="px-2 text-white/30">→</span>
        <span className="text-orange-300">{typeLabel}</span>
      </div>

      <main className="shop-product-layout mx-auto grid max-w-6xl gap-8 px-6 pb-12 md:px-10">
        {/* IMAGE */}
        <section>
          <div
            className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl border border-white/10"
            style={{
              background:
                "radial-gradient(circle at 50% 35%, rgba(255,140,0,0.08), transparent 65%), #0a0a12",
            }}
            data-testid="shop-product-image"
          >
            <div
              className="absolute inset-0 flex items-center justify-center font-black tracking-tighter"
              style={{ color: "rgba(255,140,0,0.05)", fontSize: "clamp(7rem, 18vw, 14rem)" }}
              aria-hidden="true"
            >
              ₿
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <div className="text-xl font-black tracking-tight text-white/35">
                RE<span className="text-orange-400/70">₿</span>EL™
              </div>
              <div
                className="h-20 w-20 rounded-xl"
                style={{
                  background: product.colorHex,
                  boxShadow: "0 0 0 1px rgba(255,255,255,0.14), 0 16px 40px rgba(0,0,0,0.6)",
                }}
              />
              <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/45">
                {product.color}
              </div>
            </div>

            {product.badge && (
              <span
                className="absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em]"
                style={{
                  background: product.badgeBg || "transparent",
                  color: product.badgeText || "#fff",
                  border: product.badgeBorder ? `1px solid ${product.badgeBorder}` : "1px solid rgba(255,255,255,0.1)",
                }}
              >
                {product.badge}
              </span>
            )}
          </div>

          {variants.length > 1 && (
            <div className="mt-6">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
                {t.product_more_variants}
              </p>
              <div className="flex flex-wrap gap-2.5">
                {variants.map((v) => (
                  <Link
                    key={v.id}
                    to={`/shadow-market/${v.slug}`}
                    title={v.color}
                    className="inline-flex h-8 w-8 rounded-full transition-transform hover:scale-110"
                    style={{
                      background: v.colorHex,
                      border: `2px solid ${v.id === product.id ? "#fbbf24" : "transparent"}`,
                      boxShadow: "0 0 0 1px rgba(255,255,255,0.1)",
                    }}
                    data-testid={`shop-variant-${v.id}`}
                  />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* INFO */}
        <section className="flex flex-col gap-6">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-orange-300/80">
              {typeLabel}
            </p>
            <h1
              className="mt-3 text-3xl font-black tracking-tight text-white md:text-4xl"
              data-testid="shop-product-title"
            >
              {product.name}
            </h1>
            <p className="mt-2 text-sm text-white/55">{product.subtitle}</p>
          </div>

          {/* Price */}
          <div>
            {isPro ? (
              <div className="flex items-baseline gap-3">
                <span className="text-base text-white/35 line-through">€{product.price.toFixed(2)}</span>
                <span className="bg-gradient-to-br from-orange-300 to-amber-600 bg-clip-text text-4xl font-black text-transparent">
                  €{product.proPrice.toFixed(2)}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-orange-300">
                  {t.product_pro_price}
                </span>
              </div>
            ) : (
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="text-4xl font-black text-white">€{product.price.toFixed(2)}</span>
                <Link
                  to="/academy"
                  className="text-xs font-bold tracking-tight text-orange-300 transition-colors hover:text-orange-200"
                  style={{ borderBottom: "1px solid currentColor", paddingBottom: 2 }}
                >
                  {t.product_activate_pro}
                </Link>
              </div>
            )}
          </div>

          {/* Color */}
          <div>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
              {t.product_color}: <span className="text-white">{product.color}</span>
            </p>
            <div className="flex gap-2.5">
              {variants.map((v) => (
                <Link
                  key={v.id}
                  to={`/shadow-market/${v.slug}`}
                  title={v.color}
                  className="h-8 w-8 rounded-full transition-transform hover:scale-110"
                  style={{
                    background: v.colorHex,
                    border: `2px solid ${v.id === product.id ? "#fbbf24" : "transparent"}`,
                    boxShadow: "0 0 0 1px rgba(255,255,255,0.1)",
                  }}
                />
              ))}
            </div>
          </div>

          {/* Size */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
                {t.product_size}: {selectedSize && <span className="text-white">{selectedSize}</span>}
              </p>
              <button
                onClick={() => setShowSizeGuide(true)}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-orange-300 transition-colors hover:text-orange-200"
                data-testid="shop-size-guide-btn"
              >
                <Ruler className="h-3 w-3" /> {t.product_size_guide}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {product.sizes.map((s) => {
                const out = isOut(s);
                const active = selectedSize === s;
                return (
                  <button
                    key={s}
                    disabled={out}
                    onClick={() => !out && setSelectedSize(s)}
                    className="rounded-full font-black tracking-[0.04em] transition-all"
                    style={{
                      width: 48, height: 36,
                      fontSize: "0.75rem",
                      border: `1px solid ${active ? "rgba(255,140,0,0.7)" : "rgba(255,255,255,0.12)"}`,
                      color: out ? "rgba(255,255,255,0.25)" : active ? "#fbbf24" : "rgba(255,255,255,0.55)",
                      background: active ? "rgba(255,140,0,0.10)" : "transparent",
                      textDecoration: out ? "line-through" : "none",
                      opacity: out ? 0.45 : 1,
                      cursor: out ? "not-allowed" : "pointer",
                    }}
                    data-testid={`shop-product-size-${s}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quantity */}
          <div className="flex items-center gap-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
              {t.product_quantity}:
            </p>
            <div className="inline-flex items-center overflow-hidden rounded-full border border-white/15">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 text-white/55 transition-colors hover:text-white" data-testid="shop-qty-dec">
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="min-w-[36px] text-center text-sm font-black text-white">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(20, q + 1))} className="px-3 py-2 text-white/55 transition-colors hover:text-white" data-testid="shop-qty-inc">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Add to cart */}
          <button
            onClick={handleAdd}
            className={`shop-btn-primary w-full ${added ? "opacity-90" : ""}`}
            style={{ height: 54, fontSize: "0.85rem", letterSpacing: "0.12em" }}
            data-testid="shop-product-add"
          >
            {added ? <><Check className="h-4 w-4" /> {t.product_added}</> : <><Plus className="h-4 w-4" /> {t.product_add}</>}
          </button>

          {(product.type === "polo" || product.type === "camiseta") && (
            <Link
              to={`/shadow-market/personalizar/${product.slug}`}
              className="studio-customize-cta w-full justify-center"
              style={{ padding: "12px 16px", fontSize: "0.72rem", letterSpacing: "0.16em" }}
              data-testid="shop-product-customize"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {t.customize_cta} · +€15
            </Link>
          )}

          {/* Accordions */}
          <div className="mt-2 rounded-2xl border border-white/10 bg-white/[0.02] px-5 backdrop-blur-sm">
            <Accordion title={t.acc_details} testid="shop-acc-details">
              <ul className="list-disc space-y-1 pl-5">
                {product.details.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </Accordion>
            <Accordion title={t.acc_shipping} testid="shop-acc-shipping">
              <p>{t.shipping_std(SHIPPING_COST)}</p>
              <p>{t.shipping_free_text(FREE_SHIPPING_THRESHOLD)}</p>
              <p>{t.shipping_returns}</p>
              <p>{t.shipping_contact}</p>
            </Accordion>
            <Accordion title={t.acc_care} testid="shop-acc-care">
              <p>{t.care_body}</p>
            </Accordion>
          </div>

          <Link
            to="/shadow-market"
            className="mt-2 inline-flex items-center gap-1.5 self-start text-[11px] font-bold uppercase tracking-[0.22em] text-white/45 transition-colors hover:text-orange-200"
          >
            <ArrowLeft className="h-3 w-3" /> {t.cart_continue}
          </Link>
        </section>
      </main>

      {/* SIZE GUIDE MODAL */}
      {showSizeGuide && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(6px)" }}
          onClick={() => setShowSizeGuide(false)}
          data-testid="shop-size-guide-modal"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-white/15 bg-zinc-950/95 p-6 backdrop-blur-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-black uppercase tracking-[0.18em] text-white">
                {t.size_guide_title}
              </h3>
              <button onClick={() => setShowSizeGuide(false)} className="rounded-full border border-white/10 bg-white/[0.04] p-1.5 text-white/60 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/10 text-white/45">
                  <th className="py-2 text-left">{t.size_guide_col_size}</th>
                  <th className="py-2 text-right">{t.size_guide_col_chest}</th>
                  <th className="py-2 text-right">{t.size_guide_col_length}</th>
                  <th className="py-2 text-right">{t.size_guide_col_waist}</th>
                </tr>
              </thead>
              <tbody>
                {SIZE_GUIDE.map((row) => (
                  <tr key={row.size} className="border-b border-white/5">
                    <td className="py-2 font-black text-white">{row.size}</td>
                    <td className="py-2 text-right text-white/70">{row.pecho}</td>
                    <td className="py-2 text-right text-white/70">{row.largo}</td>
                    <td className="py-2 text-right text-white/70">{row.cintura}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-4 text-[10px] text-white/40">{t.size_guide_disclaimer}</p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ShadowMarketProductPage;
